import React, { useState, useEffect, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished, BOOKMAKER_ALIASES } from '../utils/constants';
import { getEdgeForPlayerBook } from '../utils/oddsHelpers';
import GameHeader from './GameHeader';
import H2HGameModal from './H2HGameModal';
import UpgradeButton from './UpgradeButton';

// Create reverse alias map (caesars -> williamhill_us)
const REVERSE_ALIASES = Object.entries(BOOKMAKER_ALIASES).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

const SoccerDataTable = ({
  data,
  gameTime,
  gameStatus,
  gameDate,
  lastUpdate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  isMobile,
  selectedMarket,
  selectedMarketName,
  bookmakers,
  isLoading,
  availableMarkets,
  onMarketSelection,
  showTiers,
  onTierToggle,
  masterTierFilter,
  onPlayerClick,
  onPlayerContextMenu,
  onOddsClick,
  onRemoveBet,
  betslipItems = [],
  onGameClick,
  clickedCellId,
  isCollapsed = false,
  onCollapseToggle,
  onSettingsClick
}) => {
  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', sport: 'soccer', ...game };

  const handleNameContextMenu = (player) => (e) => {
    if (!onPlayerContextMenu) return;
    const teamAbbr =
      player?.teamAbv ||
      player?.teamName ||
      (player?.team === 'away' ? safeGame.away : player?.team === 'home' ? safeGame.home : player?.team) ||
      player?.teamId ||
      null;
    onPlayerContextMenu(e, safeGame.gameIDmlb, player?.playerId, player?.name, teamAbbr);
  };

  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Track rendering state
  const [isRendering, setIsRendering] = useState(false);
  const prevMarketRef = useRef(selectedMarket);

  // Game modal state
  const [showGameModal, setShowGameModal] = useState(false);

  // Detect market change
  if (prevMarketRef.current !== selectedMarket) {
    if (!isRendering) {
      setIsRendering(true);
    }
    prevMarketRef.current = selectedMarket;
  }

  // Clear rendering state after spinner has painted
  useEffect(() => {
    if (isRendering) {
      const timeoutId = setTimeout(() => {
        setIsRendering(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isRendering]);

  // Combined loading state
  const showLoading = isLoading || isRendering;

  // Block rendering heavy data while loading
  const displayData = showLoading ? [] : data;

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const booksPerPage = 5;

  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);

  // Get visible books based on platform
  const bookLogos = isMobile
    ? allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage)
    : allBookmakers;

  // Navigation functions for mobile
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToPrevBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === 0 ? totalPages - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const goToNextBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === totalPages - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Swipe handlers for mobile
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && totalPages > 1) {
        goToNextBooks();
      }
    },
    onSwipedRight: () => {
      if (isMobile && totalPages > 1) {
        goToPrevBooks();
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  const oddsCellStyle = {
    width: '80px',
    minWidth: '80px',
    maxWidth: '80px',
    textAlign: 'center',
    fontSize: '0.95rem',
    letterSpacing: '-0.025em',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  // Helper to format odds with +/- sign
  const formatOdds = (odds) => {
    if (odds === null || odds === undefined || odds === '') return '-';
    const numOdds = parseInt(odds, 10);
    if (isNaN(numOdds)) return '-';
    return numOdds > 0 ? `+${numOdds}` : numOdds.toString();
  };

  // Get cell style based on edge percentage - matches DataTable.js logic
  const getCellStyle = (price, bestBookPrice, bestBookDiff, count) => {
    const style = {
      fontWeight: 'bold',
      fontSize: '0.8rem'
    };

    // Always bold best odds when there are multiple books and it's the unique best price
    if (price === bestBookPrice && count > 1) {
      // Apply edge tier colors if they meet the criteria
      if (bestBookDiff !== 1 && bestBookDiff !== undefined && bestBookDiff !== null) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          // Tier 1: Green with yellow text
          Object.assign(style, {
            backgroundColor: 'green',
            color: 'yellow',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        } else if (diffValue >= 0.15) {
          // Tier 2: Amber with navy blue text
          Object.assign(style, {
            backgroundColor: '#FFC107',
            color: '#212529',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        } else if (diffValue >= 0.05) {
          // Tier 3: Blue with white text
          Object.assign(style, {
            backgroundColor: '#3B82F6',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '3px'
          });
        }
      }
    }

    return style;
  };

  // Helper to get player's team
  const getPlayerTeam = (player) => {
    const playerTeam = player.teamName || player.team;
    if (!playerTeam) return 'unknown';

    if (playerTeam === safeGame.away || playerTeam === safeGame.awayTeam ||
        playerTeam === safeGame.awayTeamFull) {
      return 'away';
    }
    if (playerTeam === safeGame.home || playerTeam === safeGame.homeTeam ||
        playerTeam === safeGame.homeTeamFull) {
      return 'home';
    }
    return 'unknown';
  };

  // Helper function to find best odds for a player (for mobile display)
  const findBestOdds = (player) => {
    let bestOdds = null;
    let bestBook = null;
    let bookCount = 0;

    const isBinary = player.isBinary === true;
    const fieldSuffix = isBinary ? '' : '_over';

    allBookmakers.forEach(book => {
      const aliasBook = REVERSE_ALIASES[book];
      const odds = player[`${selectedMarket}_${book}${fieldSuffix}`] ?? player[`${selectedMarket}_${aliasBook}${fieldSuffix}`];

      if (odds !== null && odds !== undefined) {
        bookCount++;
        if (!bestOdds || odds > bestOdds) {
          bestOdds = odds;
          bestBook = book;
        }
      }
    });

    const bestBookPrice = player[`${selectedMarket}_bestBookPrice`];
    const bestBookDiff = player[`${selectedMarket}_bestBookDiff`] || player[`${selectedMarket}_bestBookDiffAvg`];

    return { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount };
  };

  // Determine if the current market is binary by checking the first player
  // Binary markets (e.g., goal scorer) don't have _over suffix
  // Non-binary markets (e.g., assists) have _over suffix
  const firstPlayer = (displayData || [])[0];
  const isMarketBinary = firstPlayer?.isBinary === true;
  const marketFieldSuffix = isMarketBinary ? '' : '_over';

  // Apply tier filtering for markets with dropdown (shots, shots on target)
  // When showTiers is OFF, filter to only show selected tier value
  // When showTiers is ON, show all tiers
  let filteredDisplayData = displayData || [];
  if (!showTiers && masterTierFilter !== null && (selectedMarket === 'player_shots' || selectedMarket === 'player_shots_on_target')) {
    filteredDisplayData = filteredDisplayData.filter(player => {
      return player.point === parseFloat(masterTierFilter);
    });
  }

  // Sort function for players by best odds
  const sortByBestOdds = (a, b) => {
    const getBestOdds = (player) => {
      let bestOdds = -9999;
      allBookmakers.forEach(book => {
        const odds = player[`${selectedMarket}_${book}${marketFieldSuffix}`];
        if (odds !== null && odds !== undefined && odds > bestOdds) {
          bestOdds = odds;
        }
      });
      return bestOdds;
    };
    return getBestOdds(a) - getBestOdds(b);
  };

  // Filter and sort players by team
  const awayPlayers = (filteredDisplayData)
    .filter(p => getPlayerTeam(p) === 'away')
    .sort(sortByBestOdds);

  const homePlayers = (filteredDisplayData)
    .filter(p => getPlayerTeam(p) === 'home')
    .sort(sortByBestOdds);

  // Players with unknown team (when API doesn't provide team info)
  const unknownTeamPlayers = (filteredDisplayData)
    .filter(p => getPlayerTeam(p) === 'unknown')
    .sort(sortByBestOdds);

  // Render BTTS (Both Teams To Score) table - game-level market with yes/no
  const renderBTTSTable = () => {
    const bttsData = displayData?.[0]; // BTTS has single record per game
    if (!bttsData) return null;

    const sides = ['yes', 'no'];

    return (
      <div
        className="table-container"
        {...swipeHandlers}
        style={{
          transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
          transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
          opacity: isTransitioning ? 0.8 : 1,
          marginBottom: '15px'
        }}
      >
        <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th
                className="bg-primary text-white"
                style={{
                  width: isMobile ? '45%' : '150px',
                  textAlign: 'left',
                  padding: '8px'
                }}
              >
                <span style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                  Both Teams To Score
                </span>
              </th>
              {bookLogos.map(book => (
                <th
                  key={`header-${book}`}
                  style={{
                    ...oddsCellStyle,
                    width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width
                  }}
                >
                  <img
                    src={getBookLogoUrl(book)}
                    alt={`${book} logo`}
                    title={book}
                    style={{ height: isMobile ? '16px' : '20px' }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sides.map(side => {
              const bestBookPrice = bttsData[`btts_bestBookPrice_${side}`];
              const bestBookDiff = bttsData[`btts_bestBookDiff_${side}`] || bttsData[`btts_bestBookDiffAvg_${side}`];

              // Count books with odds for this side
              let bookCount = 0;
              allBookmakers.forEach(b => {
                const ab = REVERSE_ALIASES[b];
                if (bttsData[`btts_${b}_${side}`] !== null && bttsData[`btts_${b}_${side}`] !== undefined) {
                  bookCount++;
                } else if (ab && bttsData[`btts_${ab}_${side}`] !== null && bttsData[`btts_${ab}_${side}`] !== undefined) {
                  bookCount++;
                }
              });

              return (
                <tr key={side}>
                  <td
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: isMobile ? '0.8rem' : '0.85rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {side === 'yes' ? 'Yes' : 'No'}
                  </td>
                  {bookLogos.map(book => {
                    const aliasBook = REVERSE_ALIASES[book];
                    const odds = bttsData[`btts_${book}_${side}`] ?? bttsData[`btts_${aliasBook}_${side}`];

                    const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookCount);

                    return (
                      <td
                        key={`btts-${side}-${book}`}
                        className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, 'btts', selectedMarket, book, null, side)}`}
                        style={{
                          ...oddsCellStyle,
                          width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width,
                          cursor: odds ? 'pointer' : 'default',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          ...cellStyle,
                          opacity: bttsData[`btts_${book}_${side}_isAvailable`] === false || bttsData[`btts_${aliasBook}_${side}_isAvailable`] === false ? 0.25 : undefined
                        }}
                        onClick={() => {
                          if (odds && onOddsClick) {
                            onOddsClick(
                              safeGame.gameIDmlb,
                              'btts',
                              side === 'yes' ? 'BTTS Yes' : 'BTTS No',
                              selectedMarket,
                              book,
                              odds,
                              safeGame.away,
                              safeGame.home,
                              null,
                              null,
                              null,
                              side
                            );
                          }
                        }}
                      >
                        {formatOdds(odds)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // Each record in `records` represents a single point tier with flat keys:
  //   `${marketKey}_${book}_over`, `_under`, `_isAvailable`, plus
  //   `_bestBookPrice_${side}`, `_bestBook_${side}` ('multi' when multiple
  //   books tie), `_bestBooks_${side}` (array of tied books), `_bestBookDiff_${side}`,
  //   `_bestBooksDiff_${side}` (vs next-best, used when bestBook is 'multi').
  const buildCornersTiersFromRecords = (records, marketKey) => {
    if (!records || records.length === 0) return [];
    const tiers = [];

    records.forEach(record => {
      if (!record || record.point === null || record.point === undefined) return;
      const books = {};
      allBookmakers.forEach(b => {
        const ab = REVERSE_ALIASES[b];
        const over = record[`${marketKey}_${b}_over`] ?? (ab ? record[`${marketKey}_${ab}_over`] : undefined);
        const under = record[`${marketKey}_${b}_under`] ?? (ab ? record[`${marketKey}_${ab}_under`] : undefined);
        if (over !== undefined || under !== undefined) {
          books[b] = { over, under };
        }
      });
      if (Object.keys(books).length === 0) return;

      tiers.push({
        point: Number(record.point),
        books,
        bestBookPrice_over: record[`${marketKey}_bestBookPrice_over`],
        bestBookPrice_under: record[`${marketKey}_bestBookPrice_under`],
        bestBook_over: record[`${marketKey}_bestBook_over`],
        bestBook_under: record[`${marketKey}_bestBook_under`],
        bestBooks_over: record[`${marketKey}_bestBooks_over`] || null,
        bestBooks_under: record[`${marketKey}_bestBooks_under`] || null,
        bestBookDiff_over: record[`${marketKey}_bestBookDiff_over`],
        bestBookDiff_under: record[`${marketKey}_bestBookDiff_under`],
        bestBooksDiff_over: record[`${marketKey}_bestBooksDiff_over`],
        bestBooksDiff_under: record[`${marketKey}_bestBooksDiff_under`]
      });
    });

    return tiers.sort((a, b) => a.point - b.point);
  };

  // Render a corners O/U table from the given tier records. Each tier
  // produces an Over row and an Under row, with the point in the first column.
  const renderCornersRecordTable = (records, marketKey, headerLabel) => {
    const tiers = buildCornersTiersFromRecords(records, marketKey);
    if (tiers.length === 0) return null;

    const sides = ['over', 'under'];

    return (
      <div
        className="table-container"
        {...swipeHandlers}
        style={{
          transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
          transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
          opacity: isTransitioning ? 0.8 : 1,
          marginBottom: '15px'
        }}
      >
        <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th
                className="bg-primary text-white"
                style={{
                  width: isMobile ? '45%' : '150px',
                  textAlign: 'left',
                  padding: '8px'
                }}
              >
                <span style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                  {headerLabel}
                </span>
              </th>
              {bookLogos.map(book => (
                <th
                  key={`header-${marketKey}-${headerLabel}-${book}`}
                  style={{
                    ...oddsCellStyle,
                    width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width
                  }}
                >
                  <img
                    src={getBookLogoUrl(book)}
                    alt={`${book} logo`}
                    title={book}
                    style={{ height: isMobile ? '16px' : '20px' }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tiers.flatMap(tier => sides.map(side => {
              const bestBookPrice = tier[`bestBookPrice_${side}`];
              // When bestBook is 'multi' the per-side bestBookDiff is 0 — fall
              // back to bestBooksDiff (edge vs. next-best book) so coloring
              // still fires on multi-book ties.
              const rawDiff = tier[`bestBookDiff_${side}`];
              const bestBookDiff = (rawDiff && rawDiff !== 0)
                ? rawDiff
                : (tier[`bestBooksDiff_${side}`] || null);

              let bookCount = 0;
              allBookmakers.forEach(b => {
                const ab = REVERSE_ALIASES[b];
                const bookEntry = tier.books[b] || (ab ? tier.books[ab] : null);
                const v = bookEntry ? bookEntry[side] : undefined;
                if (v !== null && v !== undefined) bookCount++;
              });

              return (
                <tr key={`${marketKey}-${headerLabel}-${tier.point}-${side}`}>
                  <td
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: isMobile ? '0.8rem' : '0.85rem',
                      fontWeight: 'bold'
                    }}
                  >
                    <span style={{ color: side === 'over' ? '#10B981' : '#EF4444', marginRight: '4px' }}>
                      {side === 'over' ? 'O' : 'U'}
                    </span>
                    {tier.point !== null ? tier.point : ''}
                  </td>
                  {bookLogos.map(book => {
                    const aliasBook = REVERSE_ALIASES[book];
                    const bookEntry = tier.books[book] || (aliasBook ? tier.books[aliasBook] : null);
                    const odds = bookEntry ? bookEntry[side] : undefined;
                    const available = bookEntry ? bookEntry.isAvailable !== false : true;
                    const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookCount);

                    return (
                      <td
                        key={`${marketKey}-${headerLabel}-${tier.point}-${side}-${book}`}
                        className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, `${marketKey}_${headerLabel}_${side}`, marketKey, book, tier.point, side)}`}
                        style={{
                          ...oddsCellStyle,
                          width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width,
                          cursor: odds ? 'pointer' : 'default',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          ...cellStyle,
                          opacity: !available ? 0.25 : undefined
                        }}
                        onClick={() => {
                          if (odds && onOddsClick) {
                            onOddsClick(
                              safeGame.gameIDmlb,
                              null,
                              side === 'over' ? `Over ${tier.point ?? ''}`.trim() : `Under ${tier.point ?? ''}`.trim(),
                              marketKey,
                              book,
                              odds,
                              safeGame.away,
                              safeGame.home,
                              null,
                              null,
                              tier.point,
                              side
                            );
                          }
                        }}
                      >
                        {formatOdds(odds)}
                      </td>
                    );
                  })}
                </tr>
              );
            }))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Game-level: Total Corners over/under — one row-pair per point tier
  const renderCornersTable = () => {
    const records = Array.isArray(displayData) ? displayData : [];
    return renderCornersRecordTable(records, 'totals_corners', 'Total Corners');
  };

  // Team-level: one table per team. Each record is tagged by team identifier
  // (teamAbbr/team/teamName); group records by team, then render one table
  // per team with its own point-tier rows.
  const renderTeamCornersTable = () => {
    const records = Array.isArray(displayData) ? displayData : [];
    if (records.length === 0) return null;

    const teamOf = (r) => (r?.teamAbbr || r?.team || r?.teamName || '').toString();

    const byTeam = new Map();
    records.forEach(r => {
      const t = teamOf(r);
      if (!t) return;
      if (!byTeam.has(t)) byTeam.set(t, []);
      byTeam.get(t).push(r);
    });

    if (byTeam.size === 0) return null;

    const ordered = [safeGame.away, safeGame.home]
      .filter(t => byTeam.has(t))
      .concat(Array.from(byTeam.keys()).filter(t => t !== safeGame.away && t !== safeGame.home));

    return (
      <>
        {ordered.map(team => renderCornersRecordTable(
          byTeam.get(team),
          'team_totals_corners',
          `${team} Corners`
        ))}
      </>
    );
  };

  // Render H2H (Moneyline) table - game-level market with away/draw/home
  const renderH2hTable = () => {
    // Soccer h2h data comes as [{ h2h: [{...}] }] with flat structure
    // We need to find the entry with tierKey: 'h2h_3_way' for 3-way moneyline
    // Fields are like h2h_fanduel_away, h2h_fanduel_home, h2h_fanduel_draw
    const container = displayData?.[0];
    const h2hArray = container?.h2h || [];
    const h2h3WayRecord = h2hArray.find(record => record.tierKey === 'h2h_3_way');
    const h2hBinaryRecord = h2hArray.find(record => record.tierKey === 'h2h_binary');

    if (!h2h3WayRecord && !h2hBinaryRecord) return null;

    // Merge h2h_binary odds into h2h_3_way if books are missing
    // h2h_3_way is the truth, but add any books from h2h_binary that aren't in h2h_3_way
    const h2hRecord = { ...(h2h3WayRecord || h2hBinaryRecord) };

    if (h2h3WayRecord && h2hBinaryRecord) {
      // Find books in h2h_binary that aren't in h2h_3_way and add them
      const sides = ['away', 'home', 'draw'];
      Object.keys(h2hBinaryRecord).forEach(key => {
        // Match pattern h2h_{book}_{side} (e.g., h2h_fanduel_away)
        const match = key.match(/^h2h_([a-z0-9_]+)_(away|home|draw)$/);
        if (match && !key.includes('bestBook') && !key.includes('nextBook') && !key.includes('isAlternate') && !key.includes('isAvailable')) {
          const bookName = match[1];
          // Check if this book exists in h2h_3_way for any side
          const existsIn3Way = sides.some(side =>
            h2h3WayRecord[`h2h_${bookName}_${side}`] !== undefined
          );
          // If book doesn't exist in h2h_3_way, copy all its data from h2h_binary
          if (!existsIn3Way) {
            sides.forEach(side => {
              const oddsKey = `h2h_${bookName}_${side}`;
              const availKey = `h2h_${bookName}_${side}_isAvailable`;
              if (h2hBinaryRecord[oddsKey] !== undefined) {
                h2hRecord[oddsKey] = h2hBinaryRecord[oddsKey];
              }
              if (h2hBinaryRecord[availKey] !== undefined) {
                h2hRecord[availKey] = h2hBinaryRecord[availKey];
              }
            });
          }
        }
      });
    }

    if (!h2hRecord) return null;

    // Define the teams to display (away, draw, home)
    const teams = ['away', 'draw', 'home'];

    return (
      <div
        className="table-container"
        {...swipeHandlers}
        style={{
          transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
          transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
          opacity: isTransitioning ? 0.8 : 1,
          marginBottom: '15px'
        }}
      >
        <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th
                className="bg-primary text-white"
                style={{
                  width: isMobile ? '45%' : '150px',
                  textAlign: 'left',
                  padding: '8px'
                }}
              >
                <span style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                  Moneyline
                </span>
              </th>
              {bookLogos.map(book => (
                <th
                  key={`header-${book}`}
                  style={{
                    ...oddsCellStyle,
                    width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width
                  }}
                >
                  <img
                    src={getBookLogoUrl(book)}
                    alt={`${book} logo`}
                    title={book}
                    style={{ height: isMobile ? '16px' : '20px' }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teams.map(teamKey => {
              const bestBookPrice = h2hRecord[`h2h_bestBookPrice_${teamKey}`];
              const bestBookDiff = h2hRecord[`h2h_bestBookDiff_${teamKey}`] || h2hRecord[`h2h_bestBookDiffAvg_${teamKey}`];

              // Count books with odds for this team
              let bookCount = 0;
              allBookmakers.forEach(b => {
                const ab = REVERSE_ALIASES[b];
                if (h2hRecord[`h2h_${b}_${teamKey}`] !== null && h2hRecord[`h2h_${b}_${teamKey}`] !== undefined) {
                  bookCount++;
                } else if (ab && h2hRecord[`h2h_${ab}_${teamKey}`] !== null && h2hRecord[`h2h_${ab}_${teamKey}`] !== undefined) {
                  bookCount++;
                }
              });

              // Get display name for the row
              const getDisplayName = () => {
                if (teamKey === 'away') {
                  return h2hRecord.awayTeam || safeGame.awayTeamFull || safeGame.away;
                } else if (teamKey === 'home') {
                  return h2hRecord.homeTeam || safeGame.homeTeamFull || safeGame.home;
                } else if (teamKey === 'draw') {
                  return 'Draw';
                }
                return 'Unknown';
              };

              // Get logo for the row
              const getTeamLogoForEntry = () => {
                if (teamKey === 'away') {
                  return safeGame.awayLogo || getTeamLogoUrl(safeGame.away);
                } else if (teamKey === 'home') {
                  return safeGame.homeLogo || getTeamLogoUrl(safeGame.home);
                }
                return null; // No logo for draw
              };

              return (
                <tr key={teamKey}>
                  <td
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: isMobile ? '0.8rem' : '0.85rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                    onClick={() => onGameClick ? onGameClick(safeGame.gameIDmlb) : setShowGameModal(true)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {teamKey !== 'draw' && (
                        <img
                          src={getTeamLogoForEntry()}
                          alt=""
                          style={{ width: '18px', height: '18px' }}
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <span>{getDisplayName()}</span>
                    </div>
                  </td>
                  {bookLogos.map(book => {
                    const aliasBook = REVERSE_ALIASES[book];
                    // Odds are stored as h2h_${book}_${teamKey} (e.g., h2h_fanduel_away)
                    const odds = h2hRecord[`h2h_${book}_${teamKey}`] ?? h2hRecord[`h2h_${aliasBook}_${teamKey}`];

                    const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookCount);

                    return (
                      <td
                        key={`h2h-${teamKey}-${book}`}
                        className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, `h2h_3_way_${teamKey}`, 'h2h_3_way', book, null, teamKey)}`}
                        style={{
                          ...oddsCellStyle,
                          width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width,
                          cursor: odds ? 'pointer' : 'default',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          ...cellStyle,
                          opacity: h2hRecord[`h2h_${book}_${teamKey}_isAvailable`] === false || h2hRecord[`h2h_${aliasBook}_${teamKey}_isAvailable`] === false ? 0.25 : undefined
                        }}
                        onClick={() => {
                          if (odds && onOddsClick) {
                            onOddsClick(
                              safeGame.gameIDmlb,
                              null,
                              getDisplayName(),
                              'h2h_3_way',
                              book,
                              odds,
                              safeGame.away,
                              safeGame.home,
                              null,
                              null,
                              null,
                              teamKey
                            );
                          }
                        }}
                      >
                        {formatOdds(odds)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // Render Totals table - over/under for game total
  const renderTotalsTable = () => {
    const container = displayData?.[0];
    const totalsArray = container?.totals || [];

    if (totalsArray.length === 0) return null;

    // Find the main line (look for isMain flag or use most common point like 2.5)
    let mainLine = totalsArray.find(t =>
      Object.keys(t).some(k => k.includes('_isMain') && t[k] === true)
    );
    // Fallback to 2.5 point if no main line found
    if (!mainLine) {
      mainLine = totalsArray.find(t => t.point === 2.5) || totalsArray[0];
    }

    if (!mainLine) return null;

    const point = mainLine.point;
    const sides = ['over', 'under'];

    return (
      <div
        className="table-container"
        {...swipeHandlers}
        style={{
          transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
          transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
          opacity: isTransitioning ? 0.8 : 1,
          marginBottom: '15px'
        }}
      >
        <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th
                className="bg-primary text-white"
                style={{
                  width: isMobile ? '45%' : '150px',
                  textAlign: 'left',
                  padding: '8px'
                }}
              >
                <span style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                  Total ({point})
                </span>
              </th>
              {bookLogos.map(book => (
                <th
                  key={`header-totals-${book}`}
                  style={{
                    ...oddsCellStyle,
                    width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width
                  }}
                >
                  <img
                    src={getBookLogoUrl(book)}
                    alt={`${book} logo`}
                    title={book}
                    style={{ height: isMobile ? '16px' : '20px' }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sides.map(side => {
              const bestBookPrice = mainLine[`totals_bestBookPrice_${side}`];
              const bestBookDiff = mainLine[`totals_bestBookDiff_${side}`] || mainLine[`totals_bestBookDiffAvg_${side}`];

              // Count books with odds
              let bookCount = 0;
              allBookmakers.forEach(b => {
                const ab = REVERSE_ALIASES[b];
                if (mainLine[`totals_${side}_${b}`] !== null && mainLine[`totals_${side}_${b}`] !== undefined) {
                  bookCount++;
                } else if (ab && mainLine[`totals_${side}_${ab}`] !== null && mainLine[`totals_${side}_${ab}`] !== undefined) {
                  bookCount++;
                }
              });

              return (
                <tr key={side}>
                  <td
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: isMobile ? '0.8rem' : '0.85rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {side === 'over' ? `Over ${point}` : `Under ${point}`}
                  </td>
                  {bookLogos.map(book => {
                    const aliasBook = REVERSE_ALIASES[book];
                    const odds = mainLine[`totals_${side}_${book}`] ?? mainLine[`totals_${side}_${aliasBook}`];

                    const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookCount);

                    return (
                      <td
                        key={`totals-${side}-${book}`}
                        className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, `totals_${side}`, 'totals', book, point, side)}`}
                        style={{
                          ...oddsCellStyle,
                          width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width,
                          cursor: odds ? 'pointer' : 'default',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          ...cellStyle,
                          opacity: mainLine[`totals_${side}_${book}_isAvailable`] === false || mainLine[`totals_${side}_${aliasBook}_isAvailable`] === false ? 0.25 : undefined
                        }}
                        onClick={() => {
                          if (odds && onOddsClick) {
                            onOddsClick(
                              safeGame.gameIDmlb,
                              null,
                              side === 'over' ? 'Over' : 'Under',
                              'totals',
                              book,
                              odds,
                              safeGame.away,
                              safeGame.home,
                              null,
                              null,
                              point,
                              side
                            );
                          }
                        }}
                      >
                        {formatOdds(odds)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // Render Spreads table - handicap betting
  const renderSpreadsTable = () => {
    const container = displayData?.[0];
    const spreadsArray = container?.spreads || [];

    if (spreadsArray.length === 0) return null;

    // Find the main line (look for isMain flag or use 0.5 point as common soccer spread)
    let mainLine = spreadsArray.find(t =>
      Object.keys(t).some(k => k.includes('_isMain') && t[k] === true)
    );
    // Fallback to smallest non-zero point
    if (!mainLine) {
      const validSpreads = spreadsArray.filter(s => s.point > 0 && Object.keys(s).some(k => k.startsWith('spreads_') && !k.includes('bestBook')));
      validSpreads.sort((a, b) => a.point - b.point);
      mainLine = validSpreads[0];
    }

    if (!mainLine) return null;

    const point = mainLine.point;
    // For soccer spreads: plus means team gets the handicap, minus means team gives handicap
    // Show home +point and away -point (or vice versa depending on favorite)
    const rows = [
      { team: 'away', label: `${mainLine.awayTeam || safeGame.away} -${point}`, prefix: 'minus_away' },
      { team: 'home', label: `${mainLine.homeTeam || safeGame.home} +${point}`, prefix: 'plus_home' }
    ];

    return (
      <div
        className="table-container"
        {...swipeHandlers}
        style={{
          transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
          transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
          opacity: isTransitioning ? 0.8 : 1,
          marginBottom: '15px'
        }}
      >
        <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th
                className="bg-primary text-white"
                style={{
                  width: isMobile ? '45%' : '150px',
                  textAlign: 'left',
                  padding: '8px'
                }}
              >
                <span style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                  Spread
                </span>
              </th>
              {bookLogos.map(book => (
                <th
                  key={`header-spreads-${book}`}
                  style={{
                    ...oddsCellStyle,
                    width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width
                  }}
                >
                  <img
                    src={getBookLogoUrl(book)}
                    alt={`${book} logo`}
                    title={book}
                    style={{ height: isMobile ? '16px' : '20px' }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const bestBookPrice = mainLine[`spreads_bestBookPrice_${row.prefix}`];
              const bestBookDiff = mainLine[`spreads_bestBookDiff_${row.prefix}`] || mainLine[`spreads_bestBookDiffAvg_${row.prefix}`];

              // Count books with odds
              let bookCount = 0;
              allBookmakers.forEach(b => {
                const ab = REVERSE_ALIASES[b];
                if (mainLine[`spreads_${row.prefix}_${b}`] !== null && mainLine[`spreads_${row.prefix}_${b}`] !== undefined) {
                  bookCount++;
                } else if (ab && mainLine[`spreads_${row.prefix}_${ab}`] !== null && mainLine[`spreads_${row.prefix}_${ab}`] !== undefined) {
                  bookCount++;
                }
              });

              // Get logo for the row
              const teamLogo = row.team === 'away'
                ? (safeGame.awayLogo || getTeamLogoUrl(safeGame.away))
                : (safeGame.homeLogo || getTeamLogoUrl(safeGame.home));

              return (
                <tr key={row.prefix}>
                  <td
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: isMobile ? '0.8rem' : '0.85rem',
                      fontWeight: 'bold'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <img
                        src={teamLogo}
                        alt=""
                        style={{ width: '18px', height: '18px' }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <span>{row.label}</span>
                    </div>
                  </td>
                  {bookLogos.map(book => {
                    const aliasBook = REVERSE_ALIASES[book];
                    const odds = mainLine[`spreads_${row.prefix}_${book}`] ?? mainLine[`spreads_${row.prefix}_${aliasBook}`];

                    const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookCount);

                    return (
                      <td
                        key={`spreads-${row.prefix}-${book}`}
                        className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, `spreads_${row.prefix}`, 'spreads', book, point, row.team)}`}
                        style={{
                          ...oddsCellStyle,
                          width: isMobile ? `${55 / bookLogos.length}%` : oddsCellStyle.width,
                          cursor: odds ? 'pointer' : 'default',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          ...cellStyle,
                          opacity: mainLine[`spreads_${row.prefix}_${book}_isAvailable`] === false || mainLine[`spreads_${row.prefix}_${aliasBook}_isAvailable`] === false ? 0.25 : undefined
                        }}
                        onClick={() => {
                          if (odds && onOddsClick) {
                            onOddsClick(
                              safeGame.gameIDmlb,
                              `spreads_${row.prefix}`,
                              row.label,
                              'spreads',
                              book,
                              odds,
                              safeGame.away,
                              safeGame.home,
                              null,
                              null,
                              point,
                              row.team
                            );
                          }
                        }}
                      >
                        {formatOdds(odds)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // Render a team table
  const renderTeamTable = (players, teamType) => {
    const teamAbbrev = teamType === 'away' ? safeGame.away : teamType === 'home' ? safeGame.home : 'All Players';
    const teamFull = teamType === 'away' ? safeGame.awayTeamFull : teamType === 'home' ? safeGame.homeTeamFull : 'All Players';
    const teamLogo = teamType === 'away' ? safeGame.awayLogo : teamType === 'home' ? safeGame.homeLogo : null;

    if (players.length === 0) return null;

    return (
      <div
        className="table-container"
        {...swipeHandlers}
        style={{
          transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
          transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
          opacity: isTransitioning ? 0.8 : 1,
          marginBottom: '15px'
        }}
      >
        <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th
                className="bg-primary text-white"
                style={{
                  width: isMobile ? '55%' : '150px',
                  textAlign: 'left',
                  padding: '8px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {teamType !== 'all' && (
                    <img
                      src={teamLogo || getTeamLogoUrl(teamAbbrev)}
                      alt={teamAbbrev}
                      style={{ width: '20px', height: '20px' }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <span style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    {teamFull || teamAbbrev}
                  </span>
                </div>
              </th>
              {isMobile ? (
                <>
                  <th style={{ ...oddsCellStyle, width: '20%' }}>Book</th>
                  <th style={{ ...oddsCellStyle, width: '25%' }}>Odds</th>
                </>
              ) : (
                bookLogos.map(book => (
                  <th
                    key={`header-${book}`}
                    style={{
                      ...oddsCellStyle,
                      width: oddsCellStyle.width
                    }}
                  >
                    <img
                      src={getBookLogoUrl(book)}
                      alt={`${book} logo`}
                      title={book}
                      style={{ height: '20px' }}
                    />
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {players.map((player, index) => {
              const { bestOdds, bestBook, bestBookPrice, bestBookDiff, bookCount } = findBestOdds(player);

              // Determine if this is a binary market (no _over suffix needed)
              const isBinary = player.isBinary === true;
              const fieldSuffix = isBinary ? '' : '_over';

              return (
                <tr key={player.tierKey || player.playerId || index}>
                  <td
                    className="player-name-cell"
                    style={{
                      textAlign: 'left',
                      padding: '6px 8px',
                      fontSize: isMobile ? '0.8rem' : '0.85rem',
                      cursor: 'pointer',
                      backgroundImage: isMobile ? 'none' : `url(${getTeamLogoUrl(player.teamId || player.teamName || player.team)})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'left center',
                      backgroundSize: '24px 24px',
                      paddingLeft: isMobile ? '8px' : '32px'
                    }}
                    onClick={() => onPlayerClick && onPlayerClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, safeGame.away, safeGame.home, player.teamName || player.team, null)}
                    onContextMenu={handleNameContextMenu(player)}
                  >
                    {isMobile ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{formatPlayerName ? formatPlayerName(player.name, false, 'soccer') : player.name}</span>
                        {player.point !== null && player.point !== undefined && (
                          <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                            {player.point === 'yes' ? 'Yes' :
                             typeof player.point === 'number' ? `${player.point}+` : null}
                          </span>
                        )}
                      </div>
                    ) : (
                      <>
                        {formatPlayerName ? formatPlayerName(player.name, false, 'soccer') : player.name}
                        {player.point && typeof player.point === 'number' && (
                          <span style={{ color: '#6B7280', marginLeft: '4px', fontSize: '0.75rem' }}>
                            {player.point}+
                          </span>
                        )}
                      </>
                    )}
                  </td>
                  {isMobile ? (
                    // Mobile: show best book and best odds
                    bestOdds !== null && bestBook ? (
                      <>
                        <td style={{ padding: '4px', textAlign: 'center' }}>
                          <img
                            src={getBookLogoUrl(bestBook)}
                            alt={bestBook}
                            style={{ height: '16px' }}
                          />
                        </td>
                        <td
                          className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, bestBook, player.point, 'yes')}`}
                          style={{
                            padding: '6px',
                            textAlign: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            if (onOddsClick) {
                              onOddsClick(
                                safeGame.gameIDmlb,
                                player.playerId,
                                player.name,
                                selectedMarket,
                                bestBook,
                                bestOdds,
                                safeGame.away,
                                safeGame.home,
                                player.teamName || player.team,
                                null,
                                player.point,
                                'yes'
                              );
                            }
                          }}
                        >
                          <span style={getCellStyle(bestOdds, bestBookPrice, bestBookDiff, bookCount)}>
                            {bestOdds > 0 ? `+${bestOdds}` : bestOdds}
                          </span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '4px', textAlign: 'center', color: '#6B7280' }}>-</td>
                        <td style={{ padding: '4px', textAlign: 'center', color: '#6B7280' }}>-</td>
                      </>
                    )
                  ) : (
                    // Desktop: show all books
                    bookLogos.map(book => {
                      const aliasBook = REVERSE_ALIASES[book];
                      const odds = player[`${selectedMarket}_${book}${fieldSuffix}`] ?? player[`${selectedMarket}_${aliasBook}${fieldSuffix}`];
                      const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookCount);

                      return (
                        <td
                          key={`${player.playerId}-${book}`}
                          className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, player.point, 'yes')}`}
                          style={{
                            ...oddsCellStyle,
                            cursor: odds ? 'pointer' : 'default',
                            opacity: player[`${selectedMarket}_${book}${fieldSuffix}_isAvailable`] === false || player[`${selectedMarket}_${aliasBook}${fieldSuffix}_isAvailable`] === false ? 0.25 : undefined
                          }}
                          onClick={() => {
                            if (odds && onOddsClick) {
                              onOddsClick(
                                safeGame.gameIDmlb,
                                player.playerId,
                                player.name,
                                selectedMarket,
                                book,
                                odds,
                                safeGame.away,
                                safeGame.home,
                                player.teamName || player.team,
                                null,
                                player.point,
                                'yes'
                              );
                            }
                          }}
                        >
                          <span style={cellStyle}>
                            {formatOdds(odds)}
                          </span>
                        </td>
                      );
                    })
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // Check if game is locked (subscription required)
  const isLocked = safeGame.gameIDmlb?.startsWith('locked_') || safeGame.gameId?.startsWith('locked_');

  // Show skeleton if loading
  if (showLoading || !data || data.length === 0) {
    return (
      <div className="table-group">
        <GameHeader
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={safeGame}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          isLoading={showLoading}
        />

        {/* Skeleton */}
        {showLoading && (
          <div className="d-flex justify-content-center align-items-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {!showLoading && (!data || data.length === 0) && (
          <div className="text-center text-muted py-3" style={{ fontSize: '0.85rem' }}>
            {isLocked ? (
              <UpgradeButton onClick={onSettingsClick} />
            ) : (
              selectedMarket === 'h2h_3_way' ? 'No moneyline odds available for this game' :
              selectedMarket === 'btts' ? 'No BTTS odds available for this game' :
              selectedMarket === 'totals_corners' ? 'No corners odds available for this game' :
              selectedMarket === 'team_totals_corners' ? 'No team corners odds available for this game' : 'No player props available for this game'
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="table-group">
      <GameHeader
        gameTime={gameTime}
        gameStatus={gameStatus}
        game={safeGame}
        getTeamLogoUrl={getTeamLogoUrl}
        isMobile={isMobile}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        isLoading={isLoading}
      />

      {/* Mobile collapse - show only header when collapsed */}
      {isMobile && isCollapsed ? null : (
        <>
          {/* Render appropriate table based on market type */}
          {selectedMarket === 'h2h_3_way' ? (
            <>
              {renderH2hTable()}
              {renderSpreadsTable()}
              {renderTotalsTable()}
            </>
          ) : selectedMarket === 'btts' ? (
            renderBTTSTable()
          ) : selectedMarket === 'totals_corners' ? (
            renderCornersTable()
          ) : selectedMarket === 'team_totals_corners' ? (
            renderTeamCornersTable()
          ) : (
            <>
              {/* Away Team Table */}
              {renderTeamTable(awayPlayers, 'away')}

              {/* Home Team Table */}
              {renderTeamTable(homePlayers, 'home')}

              {/* Unknown Team Players (when API doesn't provide team info) */}
              {awayPlayers.length === 0 && homePlayers.length === 0 && unknownTeamPlayers.length > 0 && (
                renderTeamTable(unknownTeamPlayers, 'all')
              )}

              {/* Fallback if no players at all */}
              {awayPlayers.length === 0 && homePlayers.length === 0 && unknownTeamPlayers.length === 0 && (
                <div className="text-center text-muted py-3" style={{ fontSize: '0.85rem' }}>
                  No player props available for this game
                </div>
              )}
            </>
          )}

          {/* All Lines button for h2h market */}
          {selectedMarket === 'h2h_3_way' && (
            <div className="d-flex justify-content-center mt-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => onGameClick ? onGameClick(safeGame.gameIDmlb) : setShowGameModal(true)}
                style={{ fontSize: '0.8rem' }}
              >
                All Lines
              </button>
            </div>
          )}
        </>
      )}

      {/* Game Modal fallback — only renders when the parent didn't wire an
          onGameClick handler (which, in-app, opens the full-screen GamePanel
          takeover). Mobile keeps using this modal; desktop prefers the panel. */}
      {!onGameClick && (
        <H2HGameModal
          show={showGameModal}
          onHide={() => setShowGameModal(false)}
          game={safeGame}
          gameId={safeGame.gameIDmlb}
          sport="soccer"
          getTeamLogoUrl={getTeamLogoUrl}
          getBookLogoUrl={getBookLogoUrl}
          bookmakers={bookmakers}
          onOddsClick={onOddsClick}
          onRemoveBet={onRemoveBet}
          betslipItems={betslipItems}
        />
      )}
    </div>
  );
};

export default SoccerDataTable;
